import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const KEY = "sap-a11y";
export const TEXT_SIZES = [100, 115, 130, 150];
export const THEMES = [
  { id: "default", label: "Default", bg: "#F3EFE6", fg: "#0F3D2E" },
  { id: "cream", label: "Cream", bg: "#FBF5E2", fg: "#2C2A29" },
  { id: "blue", label: "Soft blue", bg: "#DDEBF6", fg: "#0D2238" },
  { id: "lilac", label: "Lilac", bg: "#ECE4F3", fg: "#231A2E" },
  { id: "dark", label: "Dark", bg: "#0E1513", fg: "#F1EEE6" },
  { id: "contrast", label: "High contrast", bg: "#000000", fg: "#FFE600" },
];

const prefersReduce = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
const DEFAULTS = { size: 0, theme: "default", dyslexic: false, spacing: false, links: false, reduceMotion: false, rate: 1 };

const load = () => {
  try {
    return { ...DEFAULTS, reduceMotion: prefersReduce(), ...(JSON.parse(localStorage.getItem(KEY)) || {}) };
  } catch {
    return { ...DEFAULTS, reduceMotion: prefersReduce() };
  }
};

const chunk = (text) => {
  const parts = text.replace(/\s+/g, " ").trim().split(/(?<=[.!?:])\s+/);
  const out = [];
  let cur = "";
  parts.forEach((p) => {
    if (cur && (cur + " " + p).length > 220) {
      out.push(cur);
      cur = p;
    } else cur = cur ? `${cur} ${p}` : p;
  });
  if (cur) out.push(cur);
  return out;
};

const Ctx = createContext(null);

export const A11yProvider = ({ children }) => {
  const [settings, setSettings] = useState(load);
  const [speaking, setSpeaking] = useState(false);
  const session = useRef(0);
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;

  const update = useCallback((patch) => setSettings((s) => ({ ...s, ...patch })), []);
  const reset = useCallback(() => setSettings({ ...DEFAULTS, reduceMotion: prefersReduce() }), []);

  useEffect(() => {
    const r = document.documentElement;
    r.style.fontSize = `${TEXT_SIZES[settings.size] ?? 100}%`;
    r.dataset.theme = settings.theme;
    r.classList.toggle("dark", settings.theme === "dark");
    r.classList.toggle("a11y-dyslexic", settings.dyslexic);
    r.classList.toggle("a11y-spacing", settings.spacing);
    r.classList.toggle("a11y-links", settings.links);
    r.classList.toggle("a11y-reduce", settings.reduceMotion);
    localStorage.setItem(KEY, JSON.stringify(settings));
  }, [settings]);

  const stop = useCallback(() => {
    session.current += 1;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    (text) => {
      const synth = window.speechSynthesis;
      if (!synth || !text) return;
      synth.cancel();
      const id = ++session.current;
      const voices = synth.getVoices();
      const voice = voices.find((v) => v.lang === "en-GB") || voices.find((v) => v.lang?.startsWith("en"));
      const chunks = chunk(text);
      chunks.forEach((c, i) => {
        const u = new SpeechSynthesisUtterance(c);
        if (voice) u.voice = voice;
        u.lang = "en-GB";
        u.rate = settings.rate;
        if (i === chunks.length - 1) u.onend = () => id === session.current && setSpeaking(false);
        u.onerror = () => id === session.current && setSpeaking(false);
        synth.speak(u);
      });
      setSpeaking(true);
    },
    [settings.rate]
  );

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  return (
    <Ctx.Provider value={{ settings, update, reset, speak, stop, speaking, canSpeak }}>{children}</Ctx.Provider>
  );
};

export const useA11y = () => useContext(Ctx);
